import React from 'react';
import PropTypes from 'prop-types';
import {SectionTitle, InfoItem} from '@/components/molecules';
import {RewardImages, LanguageRewardText} from '../common';
import {
  REWARD_TYPES,
  SINGLE_IMAGE_REWARD_TYPES,
  PRODUCT_BASED_REWARD_TYPES
} from '@/utils/constants/rewardsConstants';
import {
  SECTION_TITLES,
  FIELD_LABELS,
  DEFAULT_VALUES,
  CSS_CLASSES
} from '../common/ReviewConstants';

/**
 * Returns product IDs for display based on reward type (used to keep Display in sync with Setup).
 */
const getProductIds = (formData) => {
  switch (formData?.rewardType) {
    case REWARD_TYPES.BONUS_PRODUCTS:
      return (formData?.fetchedProducts || []).map((p) => p.productId);
    case REWARD_TYPES.PRODUCT:
      return (formData?.selectedProducts || []).map((p) => p.value);
    case REWARD_TYPES.PEP_VOUCHER_PRODUCT:
      return (formData?.selectedPepVouchersProducts || []).map((p) => p.value);
    default:
      return [];
  }
};

const DisplaySection = ({formData, languages}) => {
  // Check if this is a single image reward type
  const isSingleImageRewardType = SINGLE_IMAGE_REWARD_TYPES.includes(
    formData?.rewardType
  );

  // For BONUS_PRODUCTS, alertDisplayCount is at reward level (shown in Attributes section)
  const isBonusProducts = formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  const isPepVoucherProduct =
    formData?.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT;

  // Normalize data structure: for single image reward types, create productDisplay structure
  const normalizedProductDisplay = isSingleImageRewardType
    ? {
        single: {
          useDefaultImage: false,
          images:
            (formData?.imageUrls || []).length > 0
              ? [{position: 1, url: formData.imageUrls[0]}]
              : [],
          defaultImages: [],
          rewardText: formData?.rewardText || {}
        }
      }
    : formData?.display?.productDisplay || {};

  // For product-based reward types, derive the list to show from current selection so Display stays in sync with Setup
  const isProductBasedType = PRODUCT_BASED_REWARD_TYPES.includes(
    formData?.rewardType
  );

  let entriesToRender;
  if (isSingleImageRewardType) {
    entriesToRender = Object.entries(normalizedProductDisplay);
  } else if (isProductBasedType) {
    const productIds = getProductIds(formData);
    const productDisplay = formData?.display?.productDisplay || {};
    entriesToRender = productIds.map((productId) => [
      productId,
      productDisplay[productId] ?? {}
    ]);
  } else {
    entriesToRender = Object.entries(normalizedProductDisplay);
  }

  // Helper to get product name - uses different sources based on reward type
  const getProductName = (productId) => {
    if (formData?.rewardId !== '') {
      return (
        formData?.selectedProducts?.find((p) => p.value === productId)?.label ||
        productId
      );
    }
    if (isPepVoucherProduct) {
      return (
        formData?.selectedPepVouchersProducts?.find(
          (p) => p.value === productId
        )?.label || productId
      );
    }
    return (
      formData?.fetchedProducts?.find((p) => p.productId === productId)
        ?.productName || productId
    );
  };

  return (
    <div className={CSS_CLASSES.LAYOUT.SECTION_WRAPPER}>
      {entriesToRender.map(([productId, productData]) => (
        <div key={productId}>
          <div className={CSS_CLASSES.COMMON.SECTION_BORDER}>
            <SectionTitle
              id={
                isSingleImageRewardType
                  ? 'reward_card'
                  : `reward_card_${productId}`
              }
              label={SECTION_TITLES.DISPLAY.REWARD_CARD}
            />
          </div>

          <div className={CSS_CLASSES.COMMON.GRID_3_COLS}>
            {!isSingleImageRewardType && (
              <>
                <InfoItem
                  label={FIELD_LABELS.DISPLAY.PRODUCT_NAME}
                  value={getProductName(productId)}
                />
                <InfoItem
                  label={FIELD_LABELS.DISPLAY.DEFAULT_IMAGE}
                  value={
                    productData?.useDefaultImage
                      ? DEFAULT_VALUES.YES
                      : DEFAULT_VALUES.NO
                  }
                />
              </>
            )}
          </div>

          <RewardImages
            productData={productData}
            hidePositionLabel={isSingleImageRewardType}
          />

          <div className={CSS_CLASSES.COMMON.SECTION_BORDER}>
            <SectionTitle
              id={
                isSingleImageRewardType
                  ? 'reward_text'
                  : `reward_text_${productId}`
              }
              label={SECTION_TITLES.DISPLAY.REWARD_TEXT}
            />
          </div>

          <LanguageRewardText
            rewardText={
              isBonusProducts
                ? productData?.rewardText
                : formData?.rewardText || productData?.rewardText
            }
            languages={languages}
          />
        </div>
      ))}
    </div>
  );
};

DisplaySection.propTypes = {
  formData: PropTypes.shape({
    rewardId: PropTypes.string,
    selectedProducts: PropTypes.arrayOf(PropTypes.object),
    rewardType: PropTypes.string,
    imageUrls: PropTypes.arrayOf(PropTypes.string),
    rewardText: PropTypes.object,
    fetchedProducts: PropTypes.arrayOf(PropTypes.object),
    selectedPepVouchersProducts: PropTypes.arrayOf(PropTypes.object),
    display: PropTypes.shape({
      productDisplay: PropTypes.object
    })
  }),
  languages: PropTypes.arrayOf(PropTypes.object)
};

export default DisplaySection;
